// Visits oversight — read-only. Statuses + sync state + reschedule/cancel history (actor + reason +
// timestamps from appointment_events). Command does not book on a patient's behalf here.
import { useState } from "react";
import { Surface } from "@championcare/ui";
import { useApi } from "../api";
import { useAsync } from "../lib/useAsync";
import { formatDateTime } from "../lib/format";
import { Loading, ErrorState, EmptyState, PageHeader, StatusBadge } from "../components/ui";
import type { VisitHistoryRow } from "@forcebase-ai/schema/command";

export function Visits() {
  const api = useApi();
  const { data, loading, error } = useAsync(() => api.visits(), []);
  const [openId, setOpenId] = useState<string | null>(null);

  if (loading) return <Loading />;
  if (error) return <ErrorState message={error} />;
  const rows = data ?? [];

  return (
    <div className="page">
      <PageHeader title="Visits" sub="Scheduling oversight (read-only)." />
      <Surface className="card">
        {rows.length ? (
          <table className="tbl">
            <thead>
              <tr>
                <th>When</th>
                <th>Type</th>
                <th>Status</th>
                <th>EMR sync</th>
                <th>Zoom sync</th>
                <th>History</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((v) => (
                <tr key={v.id}>
                  <td className="mono">{formatDateTime(v.scheduled_at)}</td>
                  <td>{v.label.replace(/_/g, " ")}</td>
                  <td>
                    <StatusBadge value={v.status} />
                    {v.cancel_reason && <div className="muted">{v.cancel_reason}</div>}
                  </td>
                  <td>
                    <StatusBadge value={v.emr_sync_status} />
                  </td>
                  <td>
                    <StatusBadge value={v.zoom_sync_status} />
                  </td>
                  <td>
                    <button
                      className="link-btn"
                      onClick={() => setOpenId(openId === v.id ? null : v.id)}
                    >
                      {openId === v.id ? "Hide" : "View"}
                    </button>
                    {openId === v.id && <History id={v.id} />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyState>No visits.</EmptyState>
        )}
      </Surface>
    </div>
  );
}

function History({ id }: { id: string }) {
  const api = useApi();
  const { data, loading } = useAsync(() => api.visitHistory(id), [id]);
  if (loading) return <div className="muted">Loading…</div>;
  const events = (data ?? []) as VisitHistoryRow[];
  if (!events.length) return <div className="muted">No history.</div>;
  return (
    <ul className="history">
      {events.map((e) => (
        <li key={e.id}>
          <span className="history__event">{e.event.replace(/_/g, " ")}</span>
          <span className="muted">
            {e.actor_role ? `${e.actor_role} · ` : ""}
            {e.reason ? `${e.reason} · ` : ""}
            {formatDateTime(e.created_at)}
          </span>
        </li>
      ))}
    </ul>
  );
}
